import { useState, useEffect } from "react";
import Link from "next/link";
import { useRouter } from "next/router";
import { parseISO, format } from "date-fns";
import { titleCase } from "@lib/formatString";
import { LineItem } from "./LineItem";
import { formatPrice } from "@components/Product/lib/format-variant-price";
import { Spinner } from "@components/Spinner";
import styles from "./styles/Order.module.css";

export const Order = ({ order }) => {
  const router = useRouter();
  const [isLoading, setIsLoading] = useState(false);
  const [nextUrl, setNextUrl] = useState();

  const {
    id,
    orderNumber,
    totalPriceV2,
    fulfillmentStatus,
    processedAt,
    lineItems
  } = order;

  useEffect(() => {
    const handleRouteChange = (url) => {
      setNextUrl(url);
      setIsLoading(true)
    };

    router.events.on('routeChangeStart', handleRouteChange);

    // If the component is unmounted, unsubscribe
    // from the event with the `off` method:
    return () => {
      router.events.off('routeChangeStart', handleRouteChange);
      setNextUrl();
      setIsLoading(false);
    }
  }, [])

  const parsedDate = parseISO(processedAt);
  const date = format(parsedDate, "LLLL d, yyyy");

  const { currencyCode } = totalPriceV2;
  const formattedTotal = formatPrice({ amount: totalPriceV2.amount, currencyCode }, 1);

  const HeaderItem = ({ title, value }) => {
    return (
      <div className={styles.headerItem}>
        <p>{title}</p>
        <p className={styles.headerValue}>{value}</p>
      </div>
    )
  }

  const DetailsButton = ({ desktop }) => {
    const buttonStyles = `
      ${styles.button}
      ${desktop ? styles.desktopBtn : styles.mobileBtn}
    `;

    if (!id) {
      return (
        <p className={`${buttonStyles} ${styles.disabled}`}>
          Details Unavailable
        </p>
      )
    }

    return (
      <Link href={`/account/orders/${id}`}>
        <a className={buttonStyles}>
          {isLoading && nextUrl === `/account/orders/${id}` ? <Spinner /> : "View Details"}
        </a>
      </Link>
    )
  }

  const Header = () => {
    return (
      <header className={styles.header}>
        <div className={styles.headerInfo}>
          <HeaderItem title="Order number" value={orderNumber} />
          <HeaderItem title="Date placed" value={date} />
          <HeaderItem title="Status" value={titleCase(fulfillmentStatus)} />
          <HeaderItem title="Total" value={formattedTotal} />
        </div>
        {!router.query.id && <DetailsButton desktop />}
      </header>
    )
  }

  const LineItems = () => {
    return (
      <div className={styles.lineItemsContainer}>
        <div className={styles.lineItemTitles}>
          <p>Product</p>
          <p>Price/Quantity</p>
          <p>Total</p>
        </div>
        <ul>
          {lineItems.map(item => <LineItem key={item.title} item={item} />)}
        </ul>
      </div>
    )
  }

  return (
    <section className={styles.container}>
      <Header />
      <LineItems />
      {!router.query.id && <DetailsButton />}
    </section>
  )
}
